<script setup lang="ts">
import { ref } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { mdiContentSaveOffOutline, mdiContentSaveOff } from '@mdi/js'

import PromptDialog from '@/shared/components/dialog/PromptDialog.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import SlideTransition from '@/shared/components/transitions/SlideTransition.vue'
import PrimaryButton from '@/shared/components/button/PrimaryButton.vue'
import DefaultButton from '@/shared/components/button/DefaultButton.vue'
import OpacityBackdrop from '../backdrop/OpacityBackdrop.vue'

interface Props {
  open: boolean
  confirm?: boolean
}

interface Emits {
  (e: 'close'): void
}

defineOptions({ inheritAttrs: false })
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const isConfirm = ref<boolean>(false)

const onChanges = (): void => {
  if (!props.confirm) {
    emits('close')
  } else {
    isConfirm.value = true
  }
}

const onClose = (): void => {
  isConfirm.value = false
  emits('close')
}
</script>

<template>
  <OpacityBackdrop :active="open" class="z-40" />
  <SlideTransition>
    <div v-if="open" class="fixed inset-0 z-40">
      <div class="flex min-h-screen items-center justify-center p-4 overflow-auto">
        <div
          class="relative z-10 w-full max-w-2xl max-h-[90vh] bg-white dark:bg-black shadow-lg"
          :class="{ 'animate-shake': isConfirm }"
          v-on-click-outside="onChanges"
          v-bind="$attrs"
        >
          <slot></slot>
        </div>
      </div>
      <PromptDialog v-model:open="isConfirm" :title="$t('message.discardChange', 2)">
        <div class="flex justify-center">
          <SvgIcon :path="mdiContentSaveOffOutline" class="fill-gray-700 h-24 w-24" />
        </div>
        <div class="px-4 text-gray-700">
          <div class="font-medium text-md pt-4 pb-1 text-center">
            {{ $t('message.unsavedConfirm') }}
          </div>
          <div class="font-light text-sm pb-4 text-center">
            {{ $t('message.unsavedWarning') }}
          </div>
        </div>
        <div class="flex items-center justify-between p-4">
          <DefaultButton :text="$t('common.cancel')" @click="isConfirm = false" />
          <PrimaryButton
            :text="$t('common.discard')"
            :icon="mdiContentSaveOff"
            @click="onClose()"
          />
        </div>
      </PromptDialog>
    </div>
  </SlideTransition>
</template>
